import axios, { AxiosError } from "axios";
import React, { useState } from "react";
import { BigQueryResponse, Row } from "../../../../types/bigQuery.type";
import {
  endpoint,
  QUERY_ARTIST_USER_ID_LIST,
} from "../../../../module/bigQuery";
import { sendPushNotification } from "../../../../api/common.api";
import { useRecoilState, useRecoilValue } from "recoil";
import { bigQueryAccessTokenState, userListState } from "../../../../atom/atom";
import { styles } from "./styles";
import {
  ArtistNotificationResult,
  ArtistUserGroups,
  ProcessResults,
  ProcessStatus,
  RecentlyUpdatedArtist,
} from "./type";
import {
  fetchRecentlyUpdatedArtists,
  formatLogData,
  getStatusColor,
  groupUsersByArtist,
} from "./util";

type IAutomatedProcess = {
  setLogResult: (param: any) => void;
};

const AutomatedProcess: React.FC<IAutomatedProcess> = ({ setLogResult }) => {
  const [processStatus, setProcessStatus] = useState<ProcessStatus>({
    bigQuery: "idle",
    recentArtists: "idle",
    notifications: "idle",
    message: "",
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [results, setResults] = useState<ProcessResults>({
    processedArtists: [],
    totalSuccessCount: 0,
    totalErrorCount: 0,
  });
  const [totalUserList, setTotalUserList] = useRecoilState(userListState);
  const bigQueryAccessToken = useRecoilValue(bigQueryAccessTokenState);

  const updateStatus = (
    stage: keyof Omit<ProcessStatus, "message">,
    status: ProcessStatus[keyof Omit<ProcessStatus, "message">],
    message: string
  ) => {
    setProcessStatus((prev) => ({
      ...prev,
      [stage]: status,
      message,
    }));
  };

  const handleAutomatedProcess = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    setResults({
      processedArtists: [],
      totalSuccessCount: 0,
      totalErrorCount: 0,
    });

    try {
      // 1. BigQuery 실행
      updateStatus("bigQuery", "loading", "BigQuery 데이터 가져오는 중...");
      let artistUserGroups: ArtistUserGroups = {};
      try {
        const response: BigQueryResponse = await axios.post(
          endpoint,
          { query: QUERY_ARTIST_USER_ID_LIST, useLegacySql: false },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${bigQueryAccessToken}`,
            },
          }
        );
        const formattedData = formatLogData(response.data.rows);
        setLogResult(response.data.rows);
        artistUserGroups = groupUsersByArtist(formattedData);
        updateStatus("bigQuery", "success", "BigQuery 데이터 수집 완료");
      } catch (error) {
        updateStatus("bigQuery", "error", "BigQuery 데이터 수집 실패");
        throw error;
      }

      // 2. 최근 업데이트된 아티스트 조회
      updateStatus(
        "recentArtists",
        "loading",
        "최근 업데이트된 아티스트 확인 중..."
      );
      let recentArtists: RecentlyUpdatedArtist[] = [];
      try {
        const response = await fetchRecentlyUpdatedArtists();
        recentArtists = response.data;
        updateStatus(
          "recentArtists",
          "success",
          `최근 업데이트된 아티스트 ${recentArtists.length}명 확인됨`
        );
      } catch (error) {
        updateStatus(
          "recentArtists",
          "error",
          "최근 업데이트 아티스트 조회 실패"
        );
        throw error;
      }

      // 3. 알림 전송
      if (recentArtists.length > 0) {
        updateStatus("notifications", "loading", "알림 전송 중...");
        let totalSuccessCount = 0;
        let totalErrorCount = 0;
        const artistResults: ArtistNotificationResult[] = [];

        for (const artist of recentArtists) {
          const userIdList = artistUserGroups[artist.artistId] || [];
          const artistResult: ArtistNotificationResult = {
            artistId: artist.artistId,
            artistName: artist.artistName,
            successCount: 0,
            errorCount: 0,
            totalUserCount: userIdList.length,
          };

          if (userIdList.length > 0) {
            try {
              const tokens = userIdList
                .map((userId) => {
                  const user = totalUserList.find((u) => u.userId === userId);
                  return user ? user.fcmToken : null;
                })
                .filter((token): token is string => token !== null);

              if (tokens.length > 0) {
                await sendPushNotification({
                  title: `${artist.artistName} 곡 업데이트`,
                  body: `새로운 곡이 추가되었어요. 지금 바로 확인해보세요!`,
                  tokenList: tokens,
                  data: {
                    songTitle: "New Release",
                    screen: "Recent",
                    type: "곡업데이트",
                    songId: "0",
                  },
                });
                artistResult.successCount = tokens.length;
                totalSuccessCount += tokens.length;
              }
            } catch (error) {
              console.error(
                `Failed to send notifications for artist ${artist.artistName}:`,
                error
              );
              artistResult.errorCount = 1;
              totalErrorCount++;
            }
          }

          artistResults.push(artistResult);
        }

        setResults({
          processedArtists: artistResults,
          totalSuccessCount,
          totalErrorCount,
        });

        updateStatus(
          "notifications",
          "success",
          `알림 전송 완료 (성공: ${totalSuccessCount}건, 실패: ${totalErrorCount}건)`
        );
      } else {
        updateStatus(
          "notifications",
          "success",
          "최근 업데이트된 아티스트가 없습니다."
        );
      }
    } catch (error) {
      console.error("Automated process failed:", error);
      setProcessStatus((prev) => ({
        ...prev,
        message: "프로세스 실행 중 오류가 발생했습니다.",
      }));
    } finally {
      setIsProcessing(false);
    }
  };

  const ResultsSection = () => {
    if (results.processedArtists.length === 0) return null;

    return (
      <div style={styles.results}>
        <h3 style={styles.resultsTitle}>처리 결과</h3>
        <div style={styles.resultsSummary}>
          <p>
            총 처리된 알림:{" "}
            {results.totalSuccessCount + results.totalErrorCount}건
          </p>
          <p>총 성공: {results.totalSuccessCount}건</p>
          <p>총 실패: {results.totalErrorCount}건</p>
        </div>

        <div style={styles.artistResults}>
          <h4 style={styles.artistResultsTitle}>아티스트별 상세 결과</h4>
          {results.processedArtists.map((artist) => (
            <div key={artist.artistId} style={styles.artistResultItem}>
              <div style={styles.artistName}>{artist.artistName}</div>
              <div style={styles.artistStats}>
                <span style={artist.successCount > 0 ? styles.successText : {}}>
                  성공: {artist.successCount}명
                </span>
                <span style={styles.separator}>|</span>
                <span style={artist.errorCount > 0 ? styles.errorText : {}}>
                  실패: {artist.errorCount}건
                </span>
                <span style={styles.separator}>|</span>
                <span>총 대상: {artist.totalUserCount}명</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>자동화된 알림 프로세스</h2>

      <button
        onClick={handleAutomatedProcess}
        disabled={isProcessing}
        style={{
          ...styles.button,
          ...(isProcessing ? styles.buttonDisabled : {}),
        }}
      >
        {isProcessing ? "처리 중..." : "원클릭 자동 알림 프로세스 시작"}
      </button>

      {/* 진행 상황 표시 */}
      <div style={styles.statusContainer}>
        <div style={styles.statusItem}>
          <span
            style={{
              ...styles.statusDot,
              backgroundColor: getStatusColor(processStatus.bigQuery),
            }}
          />
          <span>BigQuery 데이터 수집</span>
        </div>
        <div style={styles.statusItem}>
          <span
            style={{
              ...styles.statusDot,
              backgroundColor: getStatusColor(processStatus.recentArtists),
            }}
          />
          <span>최근 업데이트 아티스트 확인</span>
        </div>
        <div style={styles.statusItem}>
          <span
            style={{
              ...styles.statusDot,
              backgroundColor: getStatusColor(processStatus.notifications),
            }}
          />
          <span>알림 전송</span>
        </div>
      </div>

      {/* 현재 상태 메시지 */}
      {processStatus.message && (
        <div style={styles.message}>{processStatus.message}</div>
      )}

      <ResultsSection />
    </div>
  );
};

export default AutomatedProcess;
